import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy,
  Flame,
  Target,
  Award,
  ArrowLeft,
  Sparkles,
  Info,
} from "lucide-react";
import { api } from "../lib/api";

/**
 * Motivasyon Kuralları — static explainer page describing how XP, streaks,
 * badges, weekly challenges and the company goal work. Reads the badge
 * catalog from the backend so it stays in sync automatically.
 */
export default function MotivationRules() {
  const [catalog, setCatalog] = useState([]);
  const [challenges, setChallenges] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [me, ch] = await Promise.all([
          api.get("/motivation/me"),
          api.get("/motivation/challenges"),
        ]);
        setCatalog(me.data.catalog || []);
        setChallenges(ch.data || []);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="motivation-rules-page">
      <div>
        <Link
          to="/app/motivasyon"
          className="mb-3 inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900"
        >
          <ArrowLeft className="h-3 w-3" />
          Motivasyon Panel&apos;e dön
        </Link>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">
          Ayarlar
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
          Motivasyon Kuralları
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Sistem nasıl çalışıyor, XP ve rozetler nasıl kazanılır — burada.
        </p>
      </div>

      {/* Section: XP */}
      <Section
        icon={<Trophy className="h-5 w-5 text-amber-500" />}
        title="XP (Deneyim Puanı)"
        subtitle="Görev tamamlayarak XP topluyorsun. Her XP seviye barına eklenir."
      >
        <RuleRow
          left="Normal görev tamamlama"
          right={<Chip value="+10 XP" tone="amber" />}
        />
        <RuleRow
          left="Yüksek öncelikli (high) görev tamamlama"
          right={<Chip value="+25 XP" tone="red" />}
        />
        <RuleRow
          left="Görevi son tarihinden ÖNCE bitirme bonusu"
          right={<Chip value="+5 XP" tone="emerald" />}
        />
        <RuleRow
          left="Haftalık meydan okuma tamamlama"
          right={<Chip value="+100 ~ +150 XP" tone="blue" />}
        />
        <p className="mt-3 rounded-md bg-zinc-50 p-3 text-xs text-zinc-600">
          <Info className="mr-1 inline h-3 w-3" /> XP yalnızca görev
          durumu <b>done</b> olduğunda verilir. Tekrar açıp kapatırsan XP tekrar
          verilmez.
        </p>
      </Section>

      {/* Section: Levels */}
      <Section
        icon={<Award className="h-5 w-5 text-purple-500" />}
        title="Seviyeler"
        subtitle="XP arttıkça yeni bir tier'e (rütbe) yükselirsin."
      >
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            { name: "Yeni Başlayan", min: 0, tier: "bronze" },
            { name: "Bronz", min: 100, tier: "bronze" },
            { name: "Gümüş", min: 300, tier: "silver" },
            { name: "Altın", min: 700, tier: "gold" },
            { name: "Platin", min: 1500, tier: "platinum" },
            { name: "Elmas", min: 3500, tier: "diamond" },
            { name: "Efsane", min: 7500, tier: "legend" },
          ].map((l) => (
            <div
              key={l.name}
              className={[
                "rounded-lg border p-3 text-center",
                TIER_BG[l.tier] || "border-zinc-200 bg-zinc-50",
              ].join(" ")}
            >
              <div className="text-xl">🏆</div>
              <div className="mt-1 text-sm font-semibold">{l.name}</div>
              <div className="text-[10px] text-zinc-600">{l.min}+ XP</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Section: Streak */}
      <Section
        icon={<Flame className="h-5 w-5 text-red-500" />}
        title="Streak (Ardışık Gün)"
        subtitle="Her gün en az 1 görev tamamlarsan streak'in büyür."
      >
        <ul className="list-disc space-y-1 pl-5 text-sm text-zinc-700">
          <li>
            <b>Streak nasıl artar?</b> Bir gün içinde en az 1 görev
            tamamladığında streak +1 olur.
          </li>
          <li>
            <b>1 gün pas hakkın var!</b> Bir gün görev tamamlayamazsan streak&apos;in{" "}
            <u>bir sonraki gün</u> yine korunur; iki gün üst üste boş geçersen
            sıfırlanır.
          </li>
          <li>
            <b>En uzun streak</b> sonsuza dek kaydedilir — bir sonraki
            hedefindir.
          </li>
          <li>
            3, 7 ve 30 günde <b>özel rozetler</b> kazanırsın.
          </li>
        </ul>
      </Section>

      {/* Section: Badges */}
      <Section
        icon={<Sparkles className="h-5 w-5 text-purple-500" />}
        title="Rozetler"
        subtitle="Belirli hedeflere ulaştığında otomatik verilir."
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {catalog.map((b) => (
            <div
              key={b.code}
              data-testid={`rule-badge-${b.code}`}
              className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-white p-3"
            >
              <div className="text-2xl">{b.emoji}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-zinc-900">
                  {b.name}
                </div>
                <div className="mt-0.5 text-xs text-zinc-600">{b.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Section: Weekly challenges */}
      <Section
        icon={<Target className="h-5 w-5 text-blue-500" />}
        title="Haftalık Meydan Okumalar"
        subtitle="Her hafta yenilenir. Tamamladığında bonus XP kazanırsın."
      >
        <ul className="space-y-2 text-sm text-zinc-700">
          {challenges.length === 0 ? (
            <li className="text-zinc-500">Bu hafta için tanımlı değil.</li>
          ) : (
            challenges.map((c) => (
              <li
                key={c.code}
                className="flex items-center justify-between rounded-md bg-zinc-50 px-3 py-2"
              >
                <span>{c.title}</span>
                <Chip value={`+${c.reward_xp} XP`} tone="blue" />
              </li>
            ))
          )}
        </ul>
      </Section>

      {/* Section: Company goal */}
      <Section
        icon={<Sparkles className="h-5 w-5 text-amber-500" />}
        title="Şirket Hedefi"
        subtitle="Tüm şirketin ortak aylık XP hedefi."
      >
        <ul className="list-disc space-y-1 pl-5 text-sm text-zinc-700">
          <li>Herkesin kazandığı XP ortak havuza eklenir.</li>
          <li>
            Ay sonunda hedefe ulaşılırsa şirket kutlar — top 5 katkı sağlayan
            özel olarak vurgulanır.
          </li>
          <li>
            Süper yönetici hedef değerini{" "}
            <b>Motivasyon Panel → Şirket Hedefi</b> alanından güncelleyebilir
            (varsayılan 2000 XP).
          </li>
        </ul>
      </Section>

      {/* Section: Leaderboard */}
      <Section
        icon={<Trophy className="h-5 w-5 text-amber-500" />}
        title="Liderlik Tablosu"
        subtitle="Şirket içinde rekabetçi (ve motive edici) sıralama."
      >
        <ul className="list-disc space-y-1 pl-5 text-sm text-zinc-700">
          <li>Herkes tablodaki her satırı görür (şeffaflık).</li>
          <li>
            Sıralama toplam XP&apos;ye göredir. İlk 3&apos;e altın/gümüş/bronz madalya.
          </li>
          <li>Kendi satırın altın arka planla vurgulanır.</li>
        </ul>
      </Section>

      <div className="pt-4">
        <Link
          to="/app/motivasyon"
          className="inline-flex items-center gap-2 rounded-md bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800"
        >
          <Trophy className="h-4 w-4" />
          Motivasyon Panel&apos;e git
        </Link>
      </div>
    </div>
  );
}

function Section({ icon, title, subtitle, children }) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
      <div className="mb-3 flex items-start gap-3">
        <div className="mt-0.5">{icon}</div>
        <div>
          <h2 className="font-display text-lg font-semibold text-zinc-900">
            {title}
          </h2>
          <p className="text-xs text-zinc-500">{subtitle}</p>
        </div>
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function RuleRow({ left, right }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-100 py-2 text-sm last:border-b-0">
      <span className="text-zinc-700">{left}</span>
      <span>{right}</span>
    </div>
  );
}

const CHIP_TONES = {
  amber: "bg-amber-100 text-amber-800 ring-amber-200",
  red: "bg-red-100 text-red-800 ring-red-200",
  emerald: "bg-emerald-100 text-emerald-800 ring-emerald-200",
  blue: "bg-blue-100 text-blue-800 ring-blue-200",
};

function Chip({ value, tone = "amber" }) {
  return (
    <span
      className={[
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1",
        CHIP_TONES[tone] || CHIP_TONES.amber,
      ].join(" ")}
    >
      {value}
    </span>
  );
}

const TIER_BG = {
  bronze: "border-orange-300 bg-orange-50",
  silver: "border-zinc-300 bg-zinc-50",
  gold: "border-amber-300 bg-amber-50",
  platinum: "border-cyan-300 bg-cyan-50",
  diamond: "border-purple-300 bg-purple-50",
  legend: "border-pink-300 bg-gradient-to-br from-pink-50 to-amber-50",
};
